import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { ComboRecipeOut, DrinkOut, DrinkTypeOut, ComboRecipeIngredientInput, EventOut } from '@/types/api';
import { canAccessAllEvents, isOrganizer, isSuperAdmin } from '@/utils/roleUtils';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import './WarehouseRecipes.scss';

interface IngredientRow extends ComboRecipeIngredientInput {
  key: string;
}

const emptyIngredientRow = (): IngredientRow => ({
  key: Math.random().toString(36).slice(2),
  ingredient_drink_id: 0,
  quantity_per_serving: 0,
  unit_label: 'ml',
});

const WarehouseRecipes: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [eventId, setEventId] = useState<number | null>(user?.event_id ?? null);
  const [availableEvents, setAvailableEvents] = useState<EventOut[]>([]);

  const [drinks, setDrinks] = useState<DrinkOut[]>([]);
  const [drinkTypes, setDrinkTypes] = useState<DrinkTypeOut[]>([]);
  const [recipes, setRecipes] = useState<ComboRecipeOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [baseDrinkId, setBaseDrinkId] = useState<number | ''>('');
  const [mixerDrinkId, setMixerDrinkId] = useState<number | ''>('');
  const [ingredientRows, setIngredientRows] = useState<IngredientRow[]>([emptyIngredientRow()]);

  // Combo recipes are scoped per Event (not per Local). Warehouse staff are
  // pinned to their assigned event; organizers/super admins pick from the
  // events they manage.
  useEffect(() => {
    if (!isOrganizer(user) && !isSuperAdmin(user)) return;

    const loadEvents = async () => {
      try {
        const eventsData = canAccessAllEvents(user)
          ? await apiService.getEvents()
          : await apiService.getMyManagedEvents();
        setAvailableEvents(eventsData);
        setEventId((prev) => (prev && eventsData.some((ev) => ev.id === prev) ? prev : eventsData[0]?.id ?? null));
      } catch (err) {
        console.error('Failed to load managed events:', err);
      }
    };
    loadEvents();
  }, [user]);

  const loadData = useCallback(async () => {
    if (!eventId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [drinksData, typesData, recipesData] = await Promise.all([
        apiService.getDrinks({ event_id: eventId }),
        apiService.getDrinkTypes(),
        apiService.getComboRecipes(eventId),
      ]);
      setDrinks(drinksData);
      setDrinkTypes(typesData);
      setRecipes(recipesData);
    } catch (err) {
      console.error('Failed to load recipe data:', err);
      setError(t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [eventId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const comboRoleOf = (drink: DrinkOut): string | null => {
    const type = drinkTypes.find((dt) => dt.id === drink.type_id);
    return type?.combo_role ?? null;
  };

  const baseDrinks = drinks.filter((d) => comboRoleOf(d) === 'base');
  const mixerDrinks = drinks.filter((d) => comboRoleOf(d) === 'mixer');
  const drinkOptions = drinks.map((d) => ({ value: d.id, label: d.name }));

  const updateIngredientRow = (key: string, field: keyof ComboRecipeIngredientInput, value: string | number) => {
    setIngredientRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  };

  const removeIngredientRow = (key: string) => {
    setIngredientRows((prev) => prev.filter((row) => row.key !== key));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventId) return;
    // The searchable selects have no native `required`, so flag a missing base/mixer here.
    if (!baseDrinkId || !mixerDrinkId) {
      setError(t('errors.validation.required'));
      return;
    }

    const ingredients = ingredientRows
      .filter((row) => row.ingredient_drink_id && row.quantity_per_serving > 0)
      .map(({ ingredient_drink_id, quantity_per_serving, unit_label }) => ({ ingredient_drink_id, quantity_per_serving, unit_label }));

    if (ingredients.length === 0) {
      setError(t('errors.validation.required'));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const created = await apiService.upsertComboRecipe({
        event_id: eventId,
        base_drink_id: Number(baseDrinkId),
        mixer_drink_id: Number(mixerDrinkId),
        ingredients,
      });
      setRecipes((prev) => {
        const withoutExisting = prev.filter((r) => r.id !== created.id);
        return [...withoutExisting, created];
      });
      setBaseDrinkId('');
      setMixerDrinkId('');
      setIngredientRows([emptyIngredientRow()]);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.general'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (recipeId: number) => {
    try {
      await apiService.deleteComboRecipe(recipeId);
      setRecipes((prev) => prev.filter((r) => r.id !== recipeId));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.general'));
    }
  };

  if (!eventId) {
    return (
      <div className="warehouse-recipes">
        <Card variant="elevated">
          <p>{t('warehouse.dashboard.noEventAssigned')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="warehouse-recipes">
      <div className="warehouse-recipes__header">
        <h2 className="warehouse-recipes__title">{t('warehouse.recipes.title')}</h2>
        {availableEvents.length > 0 && (
          <SearchableSelect<number>
            className="warehouse-recipes__event-select"
            options={availableEvents.map((ev) => ({ value: ev.id, label: ev.name }))}
            value={eventId}
            onChange={(value) => setEventId(value)}
          />
        )}
      </div>

      {error && <div className="warehouse-recipes__error">{error}</div>}

      <Card variant="elevated" className="warehouse-recipes__form-card">
        <h3>{t('warehouse.recipes.newRecipe')}</h3>
        <form onSubmit={handleSubmit} className="warehouse-recipes__form">
          <div className="warehouse-recipes__row">
            <SearchableSelect<number>
              className="warehouse-recipes__field"
              label={t('warehouse.recipes.base')}
              placeholder={t('warehouse.recipes.selectDrink')}
              required
              options={baseDrinks.map((d) => ({ value: d.id, label: d.name }))}
              value={baseDrinkId === '' ? null : baseDrinkId}
              onChange={(value) => setBaseDrinkId(value ?? '')}
            />
            <SearchableSelect<number>
              className="warehouse-recipes__field"
              label={t('warehouse.recipes.mixer')}
              placeholder={t('warehouse.recipes.selectDrink')}
              required
              options={mixerDrinks.map((d) => ({ value: d.id, label: d.name }))}
              value={mixerDrinkId === '' ? null : mixerDrinkId}
              onChange={(value) => setMixerDrinkId(value ?? '')}
            />
          </div>

          <fieldset className="warehouse-recipes__ingredients">
            <legend>{t('warehouse.recipes.ingredients')}</legend>
            {ingredientRows.map((row) => (
              <div key={row.key} className="warehouse-recipes__ingredient-row">
                <SearchableSelect<number>
                  className="warehouse-recipes__ingredient-select"
                  placeholder={t('warehouse.recipes.selectDrink')}
                  clearable
                  options={drinkOptions}
                  value={row.ingredient_drink_id || null}
                  onChange={(value) => updateIngredientRow(row.key, 'ingredient_drink_id', value ?? 0)}
                />
                <input
                  type="number"
                  min={1}
                  placeholder={t('warehouse.recipes.quantity') as string}
                  value={row.quantity_per_serving || ''}
                  onChange={(e) => updateIngredientRow(row.key, 'quantity_per_serving', Number(e.target.value))}
                />
                <input
                  type="text"
                  placeholder={t('warehouse.recipes.unit') as string}
                  value={row.unit_label}
                  onChange={(e) => updateIngredientRow(row.key, 'unit_label', e.target.value)}
                />
                <Button type="button" variant="ghost" size="xs" onClick={() => removeIngredientRow(row.key)}>
                  ✕
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="xs" onClick={() => setIngredientRows((prev) => [...prev, emptyIngredientRow()])}>
              {t('warehouse.recipes.addIngredient')}
            </Button>
          </fieldset>

          <div className="warehouse-recipes__actions">
            <Button type="submit" variant="primary" disabled={submitting} loading={submitting}>
              {t('warehouse.recipes.save')}
            </Button>
          </div>
        </form>
      </Card>

      {!loading && recipes.length === 0 ? (
        <Card variant="elevated">
          <p>{t('warehouse.recipes.empty')}</p>
        </Card>
      ) : (
        <div className="warehouse-recipes__list">
          {recipes.map((recipe) => (
            <Card key={recipe.id} variant="elevated" className="warehouse-recipes__item">
              <div className="warehouse-recipes__item-header">
                <h4>{recipe.base_drink_name} + {recipe.mixer_drink_name}</h4>
                <Button variant="ghost" size="xs" onClick={() => handleDelete(recipe.id)}>
                  {t('warehouse.recipes.delete')}
                </Button>
              </div>
              <ul className="warehouse-recipes__item-ingredients">
                {recipe.ingredients.map((ing) => (
                  <li key={ing.id}>
                    {ing.quantity_per_serving} {ing.unit_label} — {ing.ingredient_name}
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default WarehouseRecipes;
